<script setup>
import { computed, nextTick, onScopeDispose, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import { OnClickOutside } from '@vueuse/components';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import GgSupportAPI from 'dashboard/api/ggSupport';
import GgCustomersAPI from 'dashboard/api/ggCustomers';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import {
  formatGgCustomerName,
  formatGgCustomerLocation,
} from './ggCustomerPresentation';

const props = defineProps({
  conversationId: { type: Number, default: null },
  contactId: { type: Number, default: null },
  modelValue: { type: Object, default: null },
  available: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  error: { type: String, default: '' },
  label: { type: String, required: true },
  inputId: { type: String, default: '' },
  hideLabel: { type: Boolean, default: false },
  placeholder: { type: String, default: '' },
  hint: { type: String, default: '' },
  customerSearchMode: { type: String, default: 'all' },
  catalog: { type: String, default: 'clientes' },
  typeId: { type: Number, default: null },
  multiple: { type: Boolean, default: false },
  selectedIds: { type: Array, default: () => [] },
  orderKinds: { type: Array, default: () => [] },
});
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();
const SEARCH_DELAY_MS = 300;
const MIN_NAME_LENGTH = 3;
const MAX_QUERY_LENGTH = 160;
const MAX_CUSTOMER_ID = 2_147_483_647;
const MIN_PHONE_DIGITS = 4;
const MAX_PHONE_DIGITS = 20;
const customerSearchBy = computed(() =>
  ['email', 'phone'].includes(props.customerSearchMode)
    ? props.customerSearchMode
    : 'general'
);
const id = computed(() => props.inputId || `gg-support-${props.catalog}`);
const translation = computed(() =>
  props.catalog === 'clientes'
    ? 'GG_SUPPORT.CUSTOMER_SEARCH'
    : 'GG_SUPPORT.CATALOG_SEARCH'
);
const emptyMessage = computed(() => {
  const customerMessages = {
    name: 'GG_CONTACT.SEARCH_NAME_EMPTY',
    document: 'GG_CONTACT.SEARCH_DOCUMENT_EMPTY',
    id: 'GG_CONTACT.SEARCH_ID_EMPTY',
    email: 'GG_CONTACT.SEARCH_EMAIL_EMPTY',
    phone: 'GG_CONTACT.SEARCH_PHONE_EMPTY',
  };
  const key =
    props.catalog === 'clientes'
      ? customerMessages[props.customerSearchMode]
      : null;
  return t(key || `${translation.value}.EMPTY`);
});
const itemKey = item => `${item.kind || ''}-${item.id}`;
const displayName = customer =>
  [
    customer.kind,
    customer.code,
    customer.id,
    props.catalog === 'clientes'
      ? formatGgCustomerName(customer.name)
      : customer.name,
  ]
    .filter(value => value !== undefined && value !== '')
    .join(' · ');
const details = customer => {
  const tradeName =
    props.catalog === 'clientes'
      ? formatGgCustomerName(customer.trade_name)
      : customer.trade_name;
  return [
    customer.trade_name !== customer.name ? tradeName : '',
    customer.document,
    customer.serial,
    props.catalog === 'clientes'
      ? formatGgCustomerLocation(customer)
      : [customer.city, customer.state].filter(Boolean).join('/'),
  ]
    .filter(Boolean)
    .join(' · ');
};
const query = ref(props.modelValue ? displayName(props.modelValue) : '');
const results = ref([]);
const nextCursor = ref(null);
const expanded = ref(false);
const searched = ref(false);
const waiting = ref(false);
const problem = ref('');
const activeIndex = ref(-1);
const inputRef = ref(null);
const listRef = ref(null);
const { run, abort, isPending } = useAbortableRequest();
const loading = computed(() => waiting.value || isPending.value);
const inactive = computed(() => props.disabled || !props.available);
let revision = 0;

const errorText = code => {
  if (code === 'CUSTOMER_ACCESS_DENIED' || code === 'SUPPORT_ACCESS_DENIED')
    return t(`${translation.value}.ACCESS_DENIED`);
  if (code === 'CUSTOMER_DIRECTORY_UNAVAILABLE')
    return t(`${translation.value}.UNAVAILABLE`);
  if (code === 'INVALID_CURSOR')
    return t(`${translation.value}.INVALID_CURSOR`);
  if (code === 'SUPPORT_RATE_LIMITED')
    return t('GG_SUPPORT.CONTEXT.RATE_LIMITED');
  return t(`${translation.value}.FAILED`);
};

const inputProblem = term => {
  if (props.catalog !== 'clientes') return '';
  if (
    props.customerSearchMode === 'id' &&
    (!/^[1-9]\d*$/.test(term) || Number(term) > MAX_CUSTOMER_ID)
  ) {
    return t('GG_CONTACT.SEARCH_ID_HINT');
  }
  if (props.customerSearchMode === 'email' && /\s/.test(term)) {
    return t('GG_CONTACT.SEARCH_EMAIL_HINT');
  }
  if (props.customerSearchMode === 'phone') {
    const digits = term.replace(/\D/g, '').length;
    if (
      !/^[0-9+()./\s-]+$/.test(term) ||
      digits < MIN_PHONE_DIGITS ||
      digits > MAX_PHONE_DIGITS
    ) {
      return t('GG_CONTACT.SEARCH_PHONE_HINT');
    }
  }
  return '';
};

const search = async (term, cursor = null, expectedRevision = revision) => {
  if (expectedRevision !== revision || inactive.value || props.modelValue)
    return;
  waiting.value = false;
  problem.value = inputProblem(term);
  if (problem.value || !term) return;
  const exactId =
    props.catalog === 'clientes' && props.customerSearchMode === 'id';
  try {
    const response = await run(signal => {
      if (props.catalog !== 'clientes') {
        return GgSupportAPI.catalogs(
          props.conversationId,
          {
            catalog: props.catalog,
            type_id: props.typeId,
            query: term,
            cursor,
          },
          { signal }
        );
      }
      if (props.contactId) {
        const criteria = exactId
          ? { customer_id: Number(term) }
          : { query: term, cursor, search_by: customerSearchBy.value };
        return GgCustomersAPI.lookup(props.contactId, criteria, { signal });
      }
      if (exactId) {
        return GgSupportAPI.customer(props.conversationId, Number(term), {
          signal,
        });
      }
      return GgSupportAPI.customers(props.conversationId, term, cursor, {
        signal,
        searchBy: customerSearchBy.value,
      });
    });
    if (!response || expectedRevision !== revision) return;
    const previous = cursor ? results.value : [];
    const items = exactId ? [response.data.item] : response.data.items;
    results.value = [
      ...new Map(
        [...previous, ...items]
          .filter(
            item =>
              (!exactId || item.id === Number(term)) &&
              !props.selectedIds.includes(item.id) &&
              (!props.orderKinds.length || props.orderKinds.includes(item.kind))
          )
          .map(item => [itemKey(item), item])
      ).values(),
    ];
    nextCursor.value = exactId ? null : response.data.next_cursor;
    searched.value = true;
  } catch (error) {
    if (expectedRevision !== revision) return;
    const code = error.response?.data?.code;
    if (code === 'CUSTOMER_NOT_FOUND') searched.value = true;
    else problem.value = errorText(code);
  }
};
const delayedSearch = useDebounceFn(search, SEARCH_DELAY_MS);

const resetSearch = () => {
  revision += 1;
  abort();
  waiting.value = false;
  results.value = [];
  nextCursor.value = null;
  activeIndex.value = -1;
  searched.value = false;
  problem.value = '';
};
const changeQuery = event => {
  query.value = event.target.value;
  resetSearch();
  emit('update:modelValue', null);
  expanded.value = true;
  const term = query.value.trim();
  problem.value = term ? inputProblem(term) : '';
  const explicitCriterion = ['id', 'email', 'phone'].includes(
    props.customerSearchMode
  );
  waiting.value =
    !problem.value &&
    (explicitCriterion
      ? term.length > 0
      : term.length >= MIN_NAME_LENGTH || /^\d+$/.test(term));
  if (waiting.value) delayedSearch(term, null, revision);
};

const choose = async customer => {
  resetSearch();
  const expectedRevision = revision;
  let selected = customer;
  if (props.catalog !== 'clientes') {
    try {
      const criteria = {
        catalog: props.catalog,
        type_id: props.typeId,
        item_id: customer.id,
      };
      if (props.catalog === 'pedidos') criteria.order_kind = customer.kind;
      const response = await run(signal =>
        GgSupportAPI.catalogs(props.conversationId, criteria, { signal })
      );
      if (!response || revision !== expectedRevision) return;
      selected = response.data.item;
    } catch {
      problem.value = t('GG_SUPPORT.CATALOG_SEARCH.FAILED');
      return;
    }
  }
  expanded.value = false;
  query.value = props.multiple ? '' : displayName(selected);
  emit('update:modelValue', selected);
};
const clear = () => {
  changeQuery({ target: { value: '' } });
  inputRef.value?.focus();
};
const handleKeydown = async event => {
  if (event.key === 'Escape' && expanded.value) {
    event.stopPropagation();
    expanded.value = false;
  } else if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
    event.preventDefault();
    expanded.value = true;
    const step = event.key === 'ArrowDown' ? 1 : -1;
    activeIndex.value = Math.max(
      0,
      Math.min(results.value.length - 1, activeIndex.value + step)
    );
    await nextTick();
    listRef.value?.children[activeIndex.value]?.scrollIntoView({
      block: 'nearest',
    });
  } else if (event.key === 'Enter') {
    event.preventDefault();
    if (expanded.value && results.value[activeIndex.value]) {
      choose(results.value[activeIndex.value]);
    } else if (query.value.trim() && !props.modelValue) {
      revision += 1;
      abort();
      expanded.value = true;
      search(query.value.trim());
    }
  }
};
watch(
  () => props.modelValue,
  (customer, previous) => {
    if (customer) {
      resetSearch();
      query.value = displayName(customer);
      expanded.value = false;
    } else if (previous && query.value === displayName(previous)) {
      // A reset from the parent must clear the label without erasing new typing.
      resetSearch();
      query.value = '';
      expanded.value = false;
    }
  }
);
watch(inactive, value => {
  if (value) {
    resetSearch();
    expanded.value = false;
  }
});
watch(
  () => [
    props.conversationId,
    props.contactId,
    props.catalog,
    props.typeId,
    props.customerSearchMode,
  ],
  () => {
    resetSearch();
    query.value = props.modelValue ? displayName(props.modelValue) : '';
    expanded.value = false;
  }
);
onScopeDispose(() => {
  revision += 1;
});
</script>

<template>
  <OnClickOutside class="flex flex-col gap-2" @trigger="expanded = false">
    <label
      :for="id"
      class="text-sm font-medium text-n-slate-12"
      :class="{ 'sr-only': hideLabel }"
    >
      {{ label }}
    </label>
    <div class="relative">
      <Icon
        icon="i-lucide-search"
        class="pointer-events-none absolute start-3 top-3 size-4 text-n-slate-10"
      />
      <input
        :id="id"
        ref="inputRef"
        role="combobox"
        type="text"
        autocomplete="off"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        :aria-controls="`${id}-results`"
        :aria-describedby="`${id}-status`"
        :aria-expanded="expanded && !inactive"
        :aria-invalid="!!error"
        :aria-activedescendant="
          expanded && results[activeIndex]
            ? `${id}-${itemKey(results[activeIndex])}`
            : undefined
        "
        :value="query"
        :title="modelValue ? displayName(modelValue) : undefined"
        :placeholder="placeholder || t(`${translation}.PLACEHOLDER`)"
        :maxlength="MAX_QUERY_LENGTH"
        :disabled="inactive"
        class="block w-full h-10 !mb-0 rounded-lg border bg-n-alpha-1 !ps-9 !pe-9 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-brand focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
        :class="error ? 'border-n-ruby-8' : 'border-n-weak'"
        @input="changeQuery"
        @focus="expanded = !modelValue"
        @keydown="handleKeydown"
      />
      <Button
        v-if="query && !inactive"
        :aria-label="t(`${translation}.CLEAR`)"
        :title="t(`${translation}.CLEAR`)"
        icon="i-lucide-x"
        color="slate"
        variant="ghost"
        size="sm"
        class="absolute end-1 top-1"
        @click="clear"
      />
    </div>
    <p
      :id="`${id}-status`"
      role="status"
      aria-live="polite"
      class="m-0 text-xs break-words"
      :class="error || problem ? 'text-n-ruby-11' : 'text-n-slate-11'"
    >
      <template v-if="error">{{ error }}</template>
      <template v-else-if="!available">
        {{ t(`${translation}.PENDING`) }}
      </template>
      <template v-else-if="problem">{{ problem }}</template>
      <template v-else-if="loading">
        {{ t(`${translation}.LOADING`) }}
      </template>
      <template v-else-if="modelValue">
        {{ details(modelValue) }}
      </template>
      <template v-else-if="searched && !results.length">
        {{ emptyMessage }}
      </template>
      <template v-else-if="expanded && results.length">
        {{ t(`${translation}.CHOOSE`) }}
      </template>
      <template v-else>{{ hint || t(`${translation}.HINT`) }}</template>
    </p>
    <template v-if="expanded && !inactive">
      <ul
        v-if="results.length"
        :id="`${id}-results`"
        ref="listRef"
        role="listbox"
        :aria-label="t(`${translation}.RESULTS`)"
        class="m-0 p-1 max-h-64 overflow-y-auto list-none rounded-lg border border-n-weak bg-n-solid-1"
      >
        <li
          v-for="(customer, index) in results"
          :id="`${id}-${itemKey(customer)}`"
          :key="itemKey(customer)"
          role="option"
          :aria-selected="index === activeIndex"
          class="flex flex-col gap-1 rounded-md p-2 cursor-pointer break-words hover:bg-n-alpha-2"
          :class="{ 'bg-n-blue-2': index === activeIndex }"
          @mousedown.prevent
          @click="choose(customer)"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ displayName(customer) }}
          </span>
          <span class="text-xs text-n-slate-11">{{ details(customer) }}</span>
        </li>
      </ul>
      <Button
        v-if="problem || nextCursor"
        :label="problem ? t('GG_FOLLOWUP.RETRY') : t(`${translation}.MORE`)"
        color="slate"
        variant="ghost"
        size="sm"
        :disabled="loading"
        @click="search(query.trim(), nextCursor)"
      />
    </template>
  </OnClickOutside>
</template>
